import React, { useState, useRef, useEffect } from 'react';
import { useBrandingColors } from '../../hooks/useBrandingColors';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

export type RelevanceFilter = 'high' | 'medium' | 'low' | null;

interface RelevanceFiltersProps {
  candidateCount: number;
  selectedRelevance?: RelevanceFilter;
  onRelevanceChange?: (value: RelevanceFilter) => void;
  disabled?: boolean;
  relevancePageSize?: number;
  onRelevancePageSizeChange?: (size: number) => void;
  showReset?: boolean;
  onReset?: () => void;
  scoringEnabled?: boolean;
  onScoringEnabledChange?: (enabled: boolean) => void;
  hideViewedByCompany?: boolean;
  onHideViewedByCompanyChange?: (enabled: boolean) => void;
  // REL-21: фильтр "Только мои кандидаты"
  onlyMyCandidates?: boolean;
  onOnlyMyCandidatesChange?: (enabled: boolean) => void;
}

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

export const RelevanceFilters: React.FC<RelevanceFiltersProps> = ({
  candidateCount,
  selectedRelevance = null,
  onRelevanceChange,
  disabled = false,
  relevancePageSize = 10,
  onRelevancePageSizeChange,
  showReset = false,
  onReset,
  scoringEnabled,
  onScoringEnabledChange,
  hideViewedByCompany,
  onHideViewedByCompanyChange,
  onlyMyCandidates,
  onOnlyMyCandidatesChange,
}) => {
  const colors = useBrandingColors();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [inputValue, setInputValue] = useState(String(relevancePageSize));
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync input value with relevancePageSize prop
  useEffect(() => {
    setInputValue(String(relevancePageSize));
  }, [relevancePageSize]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
        // Apply value if valid and changed, otherwise reset
        const num = parseInt(inputValue, 10);
        if (num > 0 && num <= 500 && num !== relevancePageSize) {
          onRelevancePageSizeChange?.(num);
        } else {
          setInputValue(String(relevancePageSize));
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [relevancePageSize, inputValue, onRelevancePageSizeChange]);

  const handleRelevanceClick = (value: RelevanceFilter) => {
    if (disabled || !onRelevanceChange) return;
    // Toggle off if clicking the same filter
    if (selectedRelevance === value) {
      onRelevanceChange(null);
    } else {
      onRelevanceChange(value);
    }
  };

  const handlePageSizeSelect = (size: number) => {
    if (size !== relevancePageSize) {
      onRelevancePageSizeChange?.(size);
    }
    setInputValue(String(size));
    setDropdownOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    // Open dropdown when typing
    if (!dropdownOpen) {
      setDropdownOpen(true);
    }
  };

  const handleInputFocus = () => {
    setDropdownOpen(true);
    // Select all text on focus for easy editing
    inputRef.current?.select();
  };

  const handleInputBlur = () => {
    // Apply value on blur if valid and changed
    const num = parseInt(inputValue, 10);
    if (num > 0 && num <= 500 && num !== relevancePageSize) {
      onRelevancePageSizeChange?.(num);
    } else if (!(num > 0 && num <= 500)) {
      // Reset to current value if invalid
      setInputValue(String(relevancePageSize));
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      const num = parseInt(inputValue, 10);
      if (num > 0 && num <= 500 && num !== relevancePageSize) {
        onRelevancePageSizeChange?.(num);
        setDropdownOpen(false);
        inputRef.current?.blur();
      } else if (num > 0 && num <= 500) {
        // Valid but unchanged - just close
        setDropdownOpen(false);
        inputRef.current?.blur();
      }
    } else if (e.key === 'Escape') {
      setInputValue(String(relevancePageSize));
      setDropdownOpen(false);
      inputRef.current?.blur();
    }
  };

  const relevanceOptions: { label: string; value: RelevanceFilter }[] = [
    { label: '≥85%', value: 'high' },
    { label: '70-84%', value: 'medium' },
    { label: '<70%', value: 'low' },
  ];

  return (
    <div className="flex flex-row items-center gap-[17px] px-[24px] py-[12px] bg-white rounded-[16px] w-full">
      {/* Candidate Count / Page Size Block */}
      <div className="flex flex-row items-center gap-[12px]">
        <span className="font-inter font-medium text-[14px] leading-[20px] tracking-[-0.084px] text-black">
          Количество кандидатов
        </span>

        {/* Editable input with dropdown */}
        <div className="relative" ref={dropdownRef}>
          <div 
            className="flex flex-row items-center gap-[8px] px-[12px] py-[10px] bg-white border border-[#E2E4E9] rounded-[10px] shadow-[0px_1px_2px_0px_rgba(228,229,231,0.24)] hover:border-gray-400 transition-colors cursor-pointer min-w-[80px]"
            onClick={() => {
              setDropdownOpen(!dropdownOpen);
              if (!dropdownOpen) {
                inputRef.current?.focus();
              }
            }}
          >
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              onKeyDown={handleInputKeyDown}
              onClick={(e) => e.stopPropagation()}
              className="w-[40px] font-inter font-normal text-[14px] leading-[20px] tracking-[-0.084px] text-[#0A0D14] bg-transparent border-none outline-none"
            />
            <ChevronDownIcon
              className={`h-4 w-4 text-gray-500 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}
            />
          </div>

          {dropdownOpen && (
            <div className="absolute top-full left-0 mt-1 bg-white border border-[#E2E4E9] rounded-[10px] shadow-lg z-50 min-w-[100px]">
              {PAGE_SIZE_OPTIONS.map((size, index, arr) => (
                <button
                  key={size}
                  onClick={() => handlePageSizeSelect(size)}
                  className={`w-full px-[12px] py-[8px] text-left font-inter text-[14px] hover:bg-gray-100 transition-colors ${
                    relevancePageSize === size ? 'bg-gray-50 font-medium' : ''
                  } ${index === 0 ? 'rounded-t-[10px]' : ''} ${index === arr.length - 1 ? 'rounded-b-[10px]' : ''}`}
                >
                  {size}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Relevance Block */}
      <div className="flex flex-row items-center gap-[8px]">
        <span className="font-inter font-normal text-[14px] leading-[20px] tracking-[-0.084px] text-[#525866]">
          C релевантностью:
        </span>
        <div className="flex flex-row items-center gap-[8px]">
          {relevanceOptions.map((option) => {
            const isSelected = selectedRelevance === option.value;
            const isDisabled = disabled || !onRelevanceChange;
            return (
              <button
                key={option.value}
                onClick={() => handleRelevanceClick(option.value)}
                disabled={isDisabled}
                className={`flex flex-col gap-[10px] px-[12px] py-[4px] rounded-[16px] transition-colors ${
                  isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:opacity-90'
                }`}
                style={{
                  backgroundColor: isSelected ? colors.primary : '#F3F4F6',
                  color: isSelected ? '#FFFFFF' : '#0A0D14',
                }}
              >
                <span className="font-inter font-medium text-[14px] leading-[1.5em] tracking-[-0.084px] text-center">
                  {option.label}
                </span>
              </button>
            );
          })}
        </div>
        {showReset && (
          <button
            type="button"
            onClick={() => onReset?.()}
            disabled={disabled}
            className="text-sm font-medium text-[#525866] hover:text-[#0A0D14] transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Сбросить
          </button>
        )}
      </div>

      <div className="flex items-center gap-[24px] ml-auto">
        {onHideViewedByCompanyChange && (
          <div className="flex items-center gap-[8px]">
            <span className="font-inter font-medium text-[14px] leading-[20px] tracking-[-0.084px] text-black whitespace-nowrap">
              Скрыть просмотренные резюме
            </span>
            <button
              type="button"
              onClick={() => onHideViewedByCompanyChange(!hideViewedByCompany)}
              aria-pressed={hideViewedByCompany}
              className="relative inline-flex items-center shrink-0 focus:outline-none"
              style={{
                width: 36,
                height: 20,
                borderRadius: 9999,
                background: hideViewedByCompany ? '#E16349' : '#E5E7EB',
                transition: 'background 150ms ease'
              }}
            >
              <span
                className="absolute"
                style={{
                  left: hideViewedByCompany ? 18 : 2,
                  top: 2,
                  width: 16,
                  height: 16,
                  borderRadius: 9999,
                  backgroundColor: '#FFFFFF',
                  boxShadow: '0px 1px 3px rgba(0,0,0,0.15)',
                  transition: 'left 150ms ease'
                }}
              />
            </button>
          </div>
        )}

        {onOnlyMyCandidatesChange && (
          <div className="flex items-center gap-[8px]">
            <span className="font-inter font-medium text-[14px] leading-[20px] tracking-[-0.084px] text-black whitespace-nowrap">
              Только мои кандидаты
            </span>
            <button
              type="button"
              onClick={() => onOnlyMyCandidatesChange(!onlyMyCandidates)}
              aria-label="Только мои кандидаты"
              aria-pressed={onlyMyCandidates}
              className="relative inline-flex items-center shrink-0 focus:outline-none"
              style={{
                width: 36,
                height: 20,
                borderRadius: 9999,
                background: onlyMyCandidates ? '#E16349' : '#E5E7EB',
                transition: 'background 150ms ease'
              }}
            >
              <span
                className="absolute"
                style={{
                  left: onlyMyCandidates ? 18 : 2,
                  top: 2,
                  width: 16,
                  height: 16,
                  borderRadius: 9999,
                  backgroundColor: '#FFFFFF',
                  boxShadow: '0px 1px 3px rgba(0,0,0,0.15)',
                  transition: 'left 150ms ease'
                }}
              />
            </button>
          </div>
        )}

        {onScoringEnabledChange && (
          <div className="flex items-center gap-[8px]">
            <span className="font-inter font-medium text-[14px] leading-[20px] tracking-[-0.084px] text-black whitespace-nowrap">
              AI-оценка
            </span>
            <button
              type="button"
              onClick={() => onScoringEnabledChange(!scoringEnabled)}
              aria-pressed={scoringEnabled}
              className="relative inline-flex items-center shrink-0 focus:outline-none"
              style={{
                width: 36,
                height: 20,
                borderRadius: 9999,
                background: scoringEnabled ? '#E16349' : '#E5E7EB',
                transition: 'background 150ms ease'
              }}
            >
              <span
                className="absolute"
                style={{
                  left: scoringEnabled ? 18 : 2,
                  top: 2,
                  width: 16,
                  height: 16,
                  borderRadius: 9999,
                  backgroundColor: '#FFFFFF',
                  boxShadow: '0px 1px 3px rgba(0,0,0,0.15)',
                  transition: 'left 150ms ease'
                }}
              />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

